import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    TextField
} from "@mui/material";
import { getUsers } from "../../services/UserInfo";
import { addPartnerUser } from "../../services/PartnerUserService";

interface PartnerUserDialogProps {
    isOpen: boolean;
    onClose: () => void;
    partnerId: string;
    existingPartnerUsers: string[];
    onSave: () => void;
}

const PartnerUserDialog: React.FC<PartnerUserDialogProps> = ({
    isOpen,
    onClose,
    partnerId,
    existingPartnerUsers,
    onSave,
}) => {
    const [users, setUsers] = useState<{ Id: string; FirstName: string; LastName: string; Email: string }[]>([]);
    const [selectedUser, setSelectedUser] = useState<string>("");
    const [sponsorAdmin, setSponsorAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState<string>("");

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const allUsers = await getUsers();
                const filteredUsers = allUsers
                    .filter(user => user.Id && !existingPartnerUsers.includes(user.Id))
                    .map(user => ({
                        Id: user.Id!,
                        FirstName: user.FirstName || "Unknown",
                        LastName: user.LastName || "Unknown",
                        Email: user.Email || "No Email",
                    }));
                setUsers(filteredUsers);
            } catch (error) {
                console.error("Error fetching users:", error);
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchUsers().catch(error => console.error("Error fetching users:", error));
        }
    }, [isOpen, existingPartnerUsers]);

    // Filter users as the user types
    const filteredUsers = users.filter(user =>
        `${user.FirstName} ${user.LastName} ${user.Email}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );

    // **Fix: Reset selectedUser if it's not in the filtered list**
    useEffect(() => {
        if (selectedUser && !filteredUsers.some(user => user.Id === selectedUser)) {
            setSelectedUser(""); // Clear selection if the user disappears from the list
        }
    }, [searchTerm, filteredUsers]);

    const handleSave = async () => {
        if (!selectedUser) {
            alert("Please select a user."); // Prevent saving if no user is selected
            return;
        }

        const newPartnerUser = {
            PartnerID: partnerId,
            UserID: selectedUser,
            SponsorAdmin: sponsorAdmin,
        };

        try {
            await addPartnerUser(newPartnerUser);
            onSave();
            onClose();
        } catch (error) {
            console.error("Failed to add partner user:", error);
            alert("Failed to save partner user.");
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add Partner User</DialogTitle>
            <DialogContent>
                {/* Search Input */}
                <TextField
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    label="Search Users"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <FormControl fullWidth margin="normal">
                    <InputLabel>Select User</InputLabel>
                    <Select
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                    >
                        {filteredUsers.map((user) => (
                            <MenuItem key={user.Id} value={user.Id}>
                                {`${user.FirstName} ${user.LastName} (${user.Email})`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={<Switch checked={sponsorAdmin} onChange={(e) => setSponsorAdmin(e.target.checked)} />}
                    label="Sponsor Admin"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} color="primary" disabled={loading}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PartnerUserDialog;
